import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import {
  MASTER_TOKEN_KEY,
  useMasterLoginMutation,
} from "../../../services/masterApi";
import { useForgotMasterUserPasswordMutation } from "../../../services/api";

interface MasterUserLoginProps {
  email: string;
  onBack: () => void;
}

const MasterUserLogin = ({ email, onBack }: MasterUserLoginProps) => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [masterLogin, { isLoading }] = useMasterLoginMutation();
  const [forgotPassword, { isLoading: isSendingReset }] =
    useForgotMasterUserPasswordMutation();

  const onForgotPassword = async () => {
    setErrMsg("");
    try {
      await forgotPassword({ email }).unwrap();
      setForgotSent(true);
    } catch (error: any) {
      setErrMsg(
        error?.data?.message || "Failed to send reset email. Please try again."
      );
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!password) {
      setErrMsg("Password is required");
      return;
    }
    setErrMsg("");
    try {
      const result = await masterLogin({ email, password }).unwrap();
      const token = result?.data?.token;
      if (!token) {
        setErrMsg(result?.message || "Login failed. Please try again.");
        return;
      }
      localStorage.setItem(MASTER_TOKEN_KEY, token);
      localStorage.setItem("userType", "MASTER_USER");
      localStorage.setItem("masterUser", JSON.stringify(result.data.user));
      navigate("/users");
    } catch (error: any) {
      setErrMsg(
        error?.data?.message || "Invalid email or password. Please try again."
      );
    }
  };

  if (forgotSent) {
    return (
      <div className="w-full max-w-sm flex flex-col items-center">
        <div className="w-16 h-16 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mb-6">
          <svg
            className="w-8 h-8 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        <h2 className="text-2xl font-semibold text-[#1f2937] mb-4 text-center font-['Proxima_Nova']">
          Password Reset Email Sent
        </h2>
        <p className="text-sm text-gray-500 text-center mb-6 font-['Proxima_Nova']">
          If an account exists for {email}, we&apos;ve sent a link to reset
          your password. Please check your inbox and follow the instructions
          in the email.
        </p>

        <button
          onClick={() => {
            setForgotSent(false);
            setShowForgot(false);
            setPassword("");
            setErrMsg("");
          }}
          className="bg-[#4D5874] relative mt-4 flex w-full items-center justify-center rounded-[10px] h-[52px] min-h-[52px] px-4 text-base font-medium text-white transition-all duration-200 hover:bg-[#4D5874]/90 shadow-md font-['Proxima_Nova']"
        >
          Back to Login
        </button>
      </div>
    );
  }

  if (showForgot) {
    return (
      <div className="w-full max-w-sm">
        <h2 className="text-2xl font-semibold text-[#1f2937] mb-2 text-left font-['Proxima_Nova']">
          Forgot Password
        </h2>
        <p className="text-sm text-gray-500 text-left mb-6 font-['Proxima_Nova']">
          We&apos;ll send a password reset link to your email address.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            onForgotPassword();
          }}
          className="w-full"
        >
          {/* Email (read only) */}
          <div className="relative w-full">
            <input
              id="forgotEmail"
              type="email"
              value={email}
              readOnly
              className="w-full min-h-[52px] rounded-lg border bg-gray-100 px-4 py-0 text-base text-gray-500 outline-none focus:ring-0 focus:shadow-none border-gray-300 cursor-not-allowed"
            />
          </div>

          {/* Error message */}
          {errMsg && (
            <span className="mt-0.5 block text-xs font-medium text-red-500">
              {errMsg}
            </span>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isSendingReset}
            className="bg-[#4D5874] relative mt-4 flex w-full items-center justify-center rounded-[10px] h-[52px] min-h-[52px] px-4 text-base font-medium text-white transition-all duration-200 hover:bg-[#4D5874]/90 shadow-md disabled:opacity-70 font-['Proxima_Nova']"
          >
            <span className={isSendingReset ? "opacity-0" : ""}>
              Forgot Password
            </span>
            {isSendingReset && (
              <span className="absolute h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
            )}
          </button>

          {/* Back */}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => {
                setShowForgot(false);
                setErrMsg("");
              }}
              disabled={isSendingReset}
              className="text-xs font-semibold text-[#3B755E] underline font-['Proxima_Nova']"
            >
              Cancel / Go Back
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm">
      <h2 className="text-2xl font-semibold text-[#1f2937] mb-2 text-left font-['Proxima_Nova']">
        Master User Sign In
      </h2>
      <p className="text-sm text-gray-500 text-left mb-6 font-['Proxima_Nova']">
        Enter your password to continue to the master portal.
      </p>

      <form onSubmit={onSubmit} className="w-full">
        {/* Email (read only) */}
        <div className="relative w-full">
          <input
            id="masterEmail"
            type="email"
            value={email}
            readOnly
            className="w-full min-h-[52px] rounded-lg border bg-gray-100 px-4 py-0 text-base text-gray-500 outline-none focus:ring-0 focus:shadow-none border-gray-300 cursor-not-allowed"
          />
        </div>

        {/* Password */}
        <div className="relative w-full mt-4">
          <input
            id="masterPassword"
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            placeholder=" "
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setErrMsg("");
            }}
            disabled={isLoading}
            className={`peer w-full min-h-[52px] rounded-lg border bg-white px-4 py-0 pr-12 text-base text-[#1f2937] outline-none focus:ring-0 focus:shadow-none focus:border-2 ${
              errMsg ? "border-red-500" : "border-gray-300"
            }`}
          />
          <label
            htmlFor="masterPassword"
            className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 bg-white px-1 text-base font-normal transition-all duration-200 ${
              errMsg ? "text-red-500" : "text-[#9CA3AF]"
            } peer-focus:top-0 peer-focus:-translate-y-1/2 peer-focus:text-sm peer-[:not(:placeholder-shown)]:top-0 peer-[:not(:placeholder-shown)]:-translate-y-1/2 peer-[:not(:placeholder-shown)]:text-sm`}
          >
            Password*
          </label>
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            tabIndex={-1}
            aria-label={showPassword ? "Hide password" : "Show password"}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            {showPassword ? (
              <IoEyeOffOutline size={20} />
            ) : (
              <IoEyeOutline size={20} />
            )}
          </button>
        </div>

        {/* Error message */}
        {errMsg && (
          <span className="mt-0.5 block text-xs font-medium text-red-500">
            {errMsg}
          </span>
        )}

        {/* Forgot password */}
        <div className="flex w-full justify-end mt-1">
          <button
            type="button"
            onClick={() => {
              setErrMsg("");
              setShowForgot(true);
            }}
            disabled={isLoading}
            className="text-xs font-semibold text-[#3B755E] hover:underline font-['Proxima_Nova'] disabled:opacity-70 disabled:cursor-not-allowed"
          >
            Forgot password?
          </button>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="bg-[#4D5874] relative mt-4 flex w-full items-center justify-center rounded-[10px] h-[52px] min-h-[52px] px-4 text-base font-medium text-white transition-all duration-200 hover:bg-[#4D5874]/90 shadow-md disabled:opacity-70 font-['Proxima_Nova']"
        >
          <span className={isLoading ? "opacity-0" : ""}>Sign in</span>
          {isLoading && (
            <span className="absolute h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
          )}
        </button>

        {/* Back */}
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={onBack}
            disabled={isLoading}
            className="text-xs font-semibold text-[#3B755E] underline font-['Proxima_Nova']"
          >
            Use a different email
          </button>
        </div>
      </form>
    </div>
  );
};

export default MasterUserLogin;